import { useCallback, useEffect, useMemo, useState } from "react";
import type { EqmsController } from "../../../controllers/useEqms";
import type { CreateIssueRequest, Issue, IssueType } from "../../../models/project";
import { createIssue, friendlyMessage, listIssues } from "../../../controllers/projectApi";
import type {
  CreateTraceLinkRequest,
  Document,
  TraceEndpointType,
  TraceLink,
  TraceLinkType,
} from "../../../models/eqms";
import { createTraceLink, deleteTraceLink, deriveDocument, listDocuments, listTraceLinks } from "../../../controllers/eqmsApi";

const TYPE_LABEL: Record<IssueType, string> = {
  EPIC: "에픽",
  STORY: "스토리",
  TASK: "작업",
  BUG: "버그",
  SUBTASK: "하위 작업",
  REQUIREMENT: "요구사항",
  DESIGN: "설계",
  TEST_CLASS: "테스트 클래스",
  TEST_ITEM: "테스트 항목",
};

const LINK_TYPE_LABEL: Record<TraceLinkType, string> = {
  DERIVES: "파생",
  IMPLEMENTS: "구현",
  VERIFIES: "검증",
  SATISFIES: "충족",
  REFERENCES: "참조",
  SUPERSEDES: "대체",
};
const LINK_TYPES: TraceLinkType[] = ["DERIVES", "IMPLEMENTS", "VERIFIES", "SATISFIES", "REFERENCES", "SUPERSEDES"];

const MODE_TYPES: Record<"design" | "test", IssueType[]> = {
  design: ["REQUIREMENT", "DESIGN"],
  test: ["TEST_CLASS", "TEST_ITEM"],
};

// 설계/테스트: Jira 카드 보드(요구사항/설계 또는 테스트 클래스/항목) + 추적성 매트릭스.
export function EqmsDesignTestView({ eqms, mode }: { eqms: EqmsController; mode: "design" | "test" }) {
  const projectId = eqms.projectId;
  const [issues, setIssues] = useState<Issue[]>([]);
  const [traceLinks, setTraceLinks] = useState<TraceLink[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"board" | "matrix">("board");
  const [creating, setCreating] = useState(false);
  const [deriving, setDeriving] = useState(false);
  const [deriveTitle, setDeriveTitle] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  const types = MODE_TYPES[mode];

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [allIssues, links, docs] = await Promise.all([
        listIssues(eqms.getToken, projectId, eqms.workArea ?? undefined),
        listTraceLinks(eqms.getToken, projectId),
        listDocuments(eqms.getToken, projectId),
      ]);
      setIssues(allIssues);
      setTraceLinks(links);
      setDocuments(docs);
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [eqms.getToken, projectId, eqms.workArea]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const scoped = useMemo(() => issues.filter((i) => types.includes(i.type)), [issues, types]);

  async function addCard(req: CreateIssueRequest) {
    if (!projectId) return;
    setError(null);
    try {
      await createIssue(eqms.getToken, projectId, { ...req, workAreaTypes: eqms.workArea ? [eqms.workArea] : undefined });
      await reload();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function submitDerive() {
    if (!projectId) return;
    const title = deriveTitle.trim();
    if (!title || scoped.length === 0) return;
    setError(null);
    try {
      await deriveDocument(eqms.getToken, projectId, {
        workAreaType: eqms.workArea ?? "SOFTWARE",
        title,
        format: "md",
        issueIds: scoped.map((i) => i.id),
      });
      setDeriveTitle("");
      setDeriving(false);
      setNotice(`"${title}" 문서 초안이 문서함에 생성되었습니다.`);
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function addLink(req: CreateTraceLinkRequest) {
    if (!projectId) return;
    setError(null);
    try {
      await createTraceLink(eqms.getToken, projectId, req);
      await reload();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function removeLink(id: string) {
    setError(null);
    try {
      await deleteTraceLink(eqms.getToken, id);
      setTraceLinks((cur) => cur.filter((l) => l.id !== id));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  if (!projectId) {
    return <div className="note">프로젝트를 먼저 선택하세요.</div>;
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div className="tabs" style={{ margin: 0 }}>
          <button className={`tab ${tab === "board" ? "active" : ""}`} onClick={() => setTab("board")}>
            {mode === "design" ? "설계" : "테스트"} 보드
          </button>
          <button className={`tab ${tab === "matrix" ? "active" : ""}`} onClick={() => setTab("matrix")}>
            추적성 매트릭스
          </button>
        </div>
        {tab === "board" && (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn"
              style={{ width: "auto" }}
              disabled={loading}
              onClick={() => {
                setNotice(null);
                setDeriving((d) => !d);
              }}
            >
              {deriving ? "취소" : "📄 문서 도출"}
            </button>
            <button className="btn primary" style={{ width: "auto" }} onClick={() => setCreating((c) => !c)}>
              {creating ? "취소" : "＋ 카드"}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      )}

      {notice && (
        <div className="note" style={{ background: "#eafaf0", marginBottom: 16 }}>
          <b style={{ color: "var(--green, #2e7d32)" }}>완료:</b> {notice}
        </div>
      )}

      {deriving && tab === "board" && (
        <div className="note" style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="text"
              placeholder="도출할 문서 제목"
              value={deriveTitle}
              disabled={loading}
              onChange={(e) => setDeriveTitle(e.target.value)}
              style={{ flex: 1 }}
            />
            <button
              className="btn primary"
              style={{ width: "auto" }}
              disabled={loading || !deriveTitle.trim() || scoped.length === 0}
              onClick={() => void submitDerive()}
            >
              도출
            </button>
            <button
              className="btn"
              style={{ width: "auto" }}
              disabled={loading}
              onClick={() => {
                setDeriving(false);
                setDeriveTitle("");
              }}
            >
              취소
            </button>
          </div>
          {scoped.length === 0 && (
            <div style={{ marginTop: 8, color: "var(--amber)" }}>도출할 카드가 없습니다. 먼저 카드를 추가하세요.</div>
          )}
        </div>
      )}

      {creating && tab === "board" && (
        <CreateCardForm
          types={types}
          onSubmit={async (req) => {
            await addCard(req);
            setCreating(false);
          }}
        />
      )}

      {loading ? (
        <div className="center">불러오는 중…</div>
      ) : tab === "board" ? (
        <CardBoard types={types} issues={scoped} />
      ) : (
        <TraceMatrix issues={issues} traceLinks={traceLinks} documents={documents} onAddLink={addLink} onDeleteLink={removeLink} />
      )}
    </div>
  );
}

function CardBoard({ types, issues }: { types: IssueType[]; issues: Issue[] }) {
  return (
    <div className="board">
      {types.map((t) => {
        const items = issues.filter((i) => i.type === t);
        return (
          <div className="board-col" key={t}>
            <div className="board-col-title">
              <span>{TYPE_LABEL[t]}</span>
              <span className="tag">{items.length}</span>
            </div>
            {items.map((issue) => (
              <div className="board-card" key={issue.id}>
                <div className="issue-key">{issue.key}</div>
                <div className="issue-summary">{issue.summary}</div>
              </div>
            ))}
            {items.length === 0 && (
              <div className="sdui-list-empty" style={{ padding: 12 }}>
                없음
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CreateCardForm({
  types,
  onSubmit,
}: {
  types: IssueType[];
  onSubmit: (req: CreateIssueRequest) => Promise<void>;
}) {
  const [type, setType] = useState<IssueType>(types[0]);
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!summary.trim()) return;
    setBusy(true);
    try {
      await onSubmit({ type, summary, description: description || undefined });
      setSummary("");
      setDescription("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="info-card" style={{ marginBottom: 16 }}>
      <h4>새 카드</h4>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label className="sdui-field" style={{ minWidth: 140 }}>
          <span className="sdui-label">유형</span>
          <select className="sdui-input" value={type} onChange={(e) => setType(e.target.value as IssueType)}>
            {types.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="sdui-field" style={{ flex: 2, minWidth: 220 }}>
          <span className="sdui-label">요약</span>
          <input className="sdui-input" value={summary} onChange={(e) => setSummary(e.target.value)} />
        </label>
        <button className="btn primary" style={{ width: "auto" }} disabled={!summary.trim() || busy} onClick={submit}>
          {busy ? "생성 중…" : "생성"}
        </button>
      </div>
      <label className="sdui-field" style={{ marginTop: 10 }}>
        <span className="sdui-label">설명 (선택)</span>
        <textarea className="sdui-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
    </div>
  );
}

interface MatrixColumn {
  key: string;
  label: string;
  type: TraceEndpointType;
  id: string;
}

function TraceMatrix({
  issues,
  traceLinks,
  documents,
  onAddLink,
  onDeleteLink,
}: {
  issues: Issue[];
  traceLinks: TraceLink[];
  documents: Document[];
  onAddLink: (req: CreateTraceLinkRequest) => Promise<void>;
  onDeleteLink: (id: string) => Promise<void>;
}) {
  const [addingFor, setAddingFor] = useState<string | null>(null);

  const requirements = issues.filter((i) => i.type === "REQUIREMENT");
  const designs = issues.filter((i) => i.type === "DESIGN");
  const testItems = issues.filter((i) => i.type === "TEST_ITEM");

  const columns: MatrixColumn[] = [
    ...designs.map((d): MatrixColumn => ({ key: `ISSUE:${d.id}`, label: d.key, type: "ISSUE", id: d.id })),
    ...testItems.map((t): MatrixColumn => ({ key: `ISSUE:${t.id}`, label: t.key, type: "ISSUE", id: t.id })),
    ...documents.map((d): MatrixColumn => ({ key: `DOCUMENT:${d.id}`, label: d.docNumber, type: "DOCUMENT", id: d.id })),
  ];

  function linkBetween(reqId: string, col: MatrixColumn): TraceLink | undefined {
    return traceLinks.find(
      (l) =>
        (l.sourceType === "ISSUE" && l.sourceId === reqId && l.targetType === col.type && l.targetId === col.id) ||
        (l.targetType === "ISSUE" && l.targetId === reqId && l.sourceType === col.type && l.sourceId === col.id),
    );
  }

  if (columns.length === 0) {
    return <div className="sdui-list-empty">설계/테스트 항목 또는 문서가 없어 매트릭스를 표시할 수 없습니다.</div>;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table className="sdui-table">
        <thead>
          <tr>
            <th>요구사항</th>
            {columns.map((c) => (
              <th key={c.key} className="mono">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {requirements.map((req) => {
            const hasAnyLink = columns.some((c) => linkBetween(req.id, c));
            return (
              <tr key={req.id} style={!hasAnyLink ? { background: "#fdeef0" } : undefined}>
                <td>
                  <span className="mono">{req.key}</span> {req.summary}
                  {!hasAnyLink && (
                    <span className="tag" style={{ marginLeft: 6, color: "var(--amber)" }}>
                      미추적
                    </span>
                  )}
                </td>
                {columns.map((c) => {
                  const link = linkBetween(req.id, c);
                  const cellKey = `${req.id}|${c.key}`;
                  return (
                    <td key={c.key} style={{ textAlign: "center" }}>
                      {link ? (
                        <span
                          className="tag"
                          title={link.note || undefined}
                          style={{ cursor: "pointer" }}
                          onClick={() => void onDeleteLink(link.id)}
                        >
                          {LINK_TYPE_LABEL[link.linkType]} ✕
                        </span>
                      ) : (
                        <button
                          className="btn"
                          style={{ width: "auto", padding: "2px 8px" }}
                          onClick={() => setAddingFor(addingFor === cellKey ? null : cellKey)}
                        >
                          + 링크 추가
                        </button>
                      )}
                      {addingFor === cellKey && (
                        <AddLinkInline
                          onSubmit={async (linkType, note) => {
                            await onAddLink({
                              sourceType: "ISSUE",
                              sourceId: req.id,
                              targetType: c.type,
                              targetId: c.id,
                              linkType,
                              note: note || undefined,
                            });
                            setAddingFor(null);
                          }}
                          onCancel={() => setAddingFor(null)}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {requirements.length === 0 && <div className="sdui-list-empty">요구사항이 없습니다.</div>}
    </div>
  );
}

function AddLinkInline({
  onSubmit,
  onCancel,
}: {
  onSubmit: (linkType: TraceLinkType, note: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [linkType, setLinkType] = useState<TraceLinkType>("VERIFIES");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      await onSubmit(linkType, note);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
      <select className="sdui-input" value={linkType} onChange={(e) => setLinkType(e.target.value as TraceLinkType)}>
        {LINK_TYPES.map((t) => (
          <option key={t} value={t}>
            {LINK_TYPE_LABEL[t]}
          </option>
        ))}
      </select>
      <input className="sdui-input" placeholder="메모" value={note} onChange={(e) => setNote(e.target.value)} />
      <div style={{ display: "flex", gap: 4 }}>
        <button className="btn primary" style={{ width: "auto" }} disabled={busy} onClick={submit}>
          추가
        </button>
        <button className="btn" style={{ width: "auto" }} onClick={onCancel}>
          취소
        </button>
      </div>
    </div>
  );
}
